import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaRegClock } from "react-icons/fa";
import styles from "./Viewassessment.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const OPTION_LETTERS = ["a", "b", "c", "d", "e", "f"];
const SUCCESS_REDIRECT_SECONDS = 10;

/* localStorage key — scoped per assessment + student so multiple in-progress
 * attempts don't trample each other. Persists:
 *   - answers       : { [questionId]: "a" | "b" | ... }
 *   - questionIds   : [qid, qid, ...] full question list, so beacon/recovery
 *                     paths can build the payload even if React state is gone
 *   - deadlineMs    : timer deadline so browser-restart resumes correctly */
const draftKey = (assessmentId, studentId) =>
  `assessment_draft__${assessmentId}__${studentId}`;

function readDraft(assessmentId, studentId) {
  if (!assessmentId || !studentId) return null;
  try {
    const raw = localStorage.getItem(draftKey(assessmentId, studentId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeDraft(assessmentId, studentId, patch) {
  if (!assessmentId || !studentId) return;
  try {
    const current = readDraft(assessmentId, studentId) || {};
    const next = { ...current, ...patch };
    localStorage.setItem(
      draftKey(assessmentId, studentId),
      JSON.stringify(next),
    );
  } catch {
    /* ignore quota / private-mode errors */
  }
}

function clearDraftFor(assessmentId, studentId) {
  if (!assessmentId || !studentId) return;
  try {
    localStorage.removeItem(draftKey(assessmentId, studentId));
  } catch {
    /* ignore */
  }
}

function ViewAssessmentTest() {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const client_id = clientData?.client_id || "";
  const user_id = user?.id || "";

  const assessmentId = location?.state?.ID;
  const isRemedial = location?.state?.type === "remedial";
  const studentId = user_id;

  /* ===== State ===== */
  const [data, setData] = useState(null);

  const [answers, setAnswers] = useState(() => {
    const draft = readDraft(assessmentId, studentId);
    return draft?.answers || {};
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Server-anchored timer
  const [deadlineMs, setDeadlineMs] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const submitFiredRef = useRef(false);
  const autoSubmitFiredRef = useRef(false);
  const successCountdownRef = useRef(null);

  const [successCountdown, setSuccessCountdown] = useState(
    SUCCESS_REDIRECT_SECONDS,
  );

  const [submittedSuccess, setSubmittedSuccess] = useState({
    open: false,
    message: "",
    redirectPath: "",
    redirectState: null,
  });

  const [errorModal, setErrorModal] = useState({ open: false, message: "" });

  /* ===== Persist answers to localStorage on every change ===== */
  useEffect(() => {
    writeDraft(assessmentId, studentId, { answers });
  }, [answers, assessmentId, studentId]);

  /* ===== Initial fetch ===== */
  useEffect(() => {
    if (assessmentId && studentId) {
      fetchAssessment();
    } else {
      setError("Missing assessment or student information");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId, studentId]);

  /* Builds the submission `answers` array. Always includes EVERY question
   * (with answer: null for unanswered). Question IDs come from, in order:
   *   1. an explicit list passed in (e.g. fresh API response)
   *   2. the current `data.questions` state
   *   3. the question IDs persisted in localStorage
   *
   * Returns [] only when no question list is available anywhere. */
  const buildAnswersArray = (answersMap, questionListFromApi) => {
    const fromApi =
      Array.isArray(questionListFromApi) && questionListFromApi.length
        ? questionListFromApi.map((q) => q.id)
        : null;

    const fromState =
      Array.isArray(data?.questions) && data.questions.length
        ? data.questions.map((q) => q.id)
        : null;

    const fromDraft = (() => {
      const d = readDraft(assessmentId, studentId);
      return Array.isArray(d?.questionIds) && d.questionIds.length
        ? d.questionIds
        : null;
    })();

    const questionIds = fromApi || fromState || fromDraft || [];

    return questionIds.map((qid) => ({
      question_id: qid,
      answer: answersMap?.[qid] ?? null,
    }));
  };

  /* Recovery submit on next visit if a draft is still sitting in localStorage. */
  const submitDraftFromLocalStorage = async (apiAssessmentData) => {
    const draft = readDraft(assessmentId, studentId);
    const draftAnswers = draft?.answers || {};

    const submissionAnswers = buildAnswersArray(
      draftAnswers,
      apiAssessmentData?.questions,
    );

    if (submissionAnswers.length === 0) {
      clearDraftFor(assessmentId, studentId);
      return;
    }

    try {
      const payload = {
        school_id: apiAssessmentData?.assessment?.school_id || school_id,
        student_id: studentId,
        client_id: apiAssessmentData?.assessment?.client_id || client_id,
        assessment_id: assessmentId,
        answers: submissionAnswers,
        submission_source: "auto",
        auto_submitted: true,
      };

      await AxiosInstance.post(`/book/student/submitAssessment`, payload);
    } catch (recoverErr) {
      console.warn(
        "Draft recovery submit failed (likely already submitted):",
        recoverErr?.response?.data?.message || recoverErr?.message,
      );
    } finally {
      clearDraftFor(assessmentId, studentId);
    }
  };

  const fetchAssessment = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        school_id,
        student_id: studentId,
        id: assessmentId,
      };

      const response = await AxiosInstance.post(
        `/book/student/startAssessment`,
        payload,
      );

      const apiData = response?.data?.data || {};
      const apiMessage = response?.data?.message || "";

      const alreadyDone =
        apiData?.already_submitted === true ||
        apiData?.is_completed === true ||
        apiData?.submitted === true ||
        response?.data?.code === "ALREADY_SUBMITTED" ||
        /already (completed|submitted|attempted)/i.test(apiMessage);

      if (alreadyDone) {
        await submitDraftFromLocalStorage(apiData);

        submitFiredRef.current = true;

        const redirectPath = isRemedial
          ? "/user/viewStudentRemedialResult"
          : "/user/viewStudentAssessmentResult";

        setSubmittedSuccess({
          open: true,
          message:
            apiMessage ||
            translations?.labels?.assessmentSubmittedTitle ||
            "Your assessment has been submitted successfully.",
          redirectPath,
          redirectState: {
            assessment_id: assessmentId,
            student_id: studentId,
            type: isRemedial ? "remedial" : "assessment",
          },
        });
        return;
      }

      if (response?.data?.status) {
        const payloadData = response.data.data;
        setData(payloadData);

        /* Persist the question ID list so beacon / recovery / auto-submit
         * paths always have it, even when React state is stale. */
        if (Array.isArray(payloadData?.questions)) {
          writeDraft(assessmentId, studentId, {
            questionIds: payloadData.questions.map((q) => q.id),
          });
        }

        const a = payloadData?.assessment || {};
        const durationMin = Number(a.duration ?? a.time_duration);

        if (Number.isFinite(durationMin) && durationMin > 0) {
          const existingDraft = readDraft(assessmentId, studentId);
          let computed;

          if (
            existingDraft?.deadlineMs &&
            Number.isFinite(existingDraft.deadlineMs)
          ) {
            computed = existingDraft.deadlineMs;
          } else {
            const attendedAt = a.attended_at
              ? new Date(a.attended_at)
              : a.started_at
                ? new Date(a.started_at)
                : new Date();
            computed = attendedAt.getTime() + durationMin * 60 * 1000;
            writeDraft(assessmentId, studentId, { deadlineMs: computed });
          }

          setDeadlineMs(computed);
          setSecondsLeft(
            Math.max(0, Math.floor((computed - Date.now()) / 1000)),
          );
        }
      } else {
        setError(apiMessage || "Failed to load assessment");
      }
    } catch (err) {
      console.error("Error loading assessment:", err);
      setError(
        err?.response?.data?.message || err?.message || "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ===== Auto-submit on tab close / refresh / navigation =====
   *
   * Uses sendBeacon because regular axios calls get cancelled during unload.
   * Builds the payload from buildAnswersArray, which has a localStorage
   * fallback for the question list — so the array is never empty as long
   * as the test was ever loaded successfully on this device. */
  useEffect(() => {
    if (!assessmentId || !studentId)
      return undefined;
    const fireBeacon = () => {
      if (submitFiredRef.current) return;
      const draft = readDraft(assessmentId, studentId);
      const draftAnswers = draft?.answers || {};
      const submissionAnswers = buildAnswersArray(draftAnswers);
      if (submissionAnswers.length === 0)
        return;
      const payload = {
        school_id:
          data?.assessment?.school_id ||
          school_id,
        student_id: studentId,
        client_id:
          data?.assessment?.client_id ||
          client_id,
        assessment_id: assessmentId,

        answers: submissionAnswers,

        submission_source: "auto",

        auto_submitted: true,
      };

      const baseURL = (
        AxiosInstance.defaults?.baseURL || ""
      ).replace(/\/$/, "");

      const url = `${baseURL}/book/student/submitAssessment`;

      try {
        const blob = new Blob(
          [JSON.stringify(payload)],
          {
            type: "application/json",
          }
        );

        navigator.sendBeacon(url, blob);

        submitFiredRef.current = true;
      } catch {
        //
      }
    };

    // ONLY browser close / refresh
    window.addEventListener(
      "beforeunload",
      fireBeacon
    );

    window.addEventListener(
      "pagehide",
      fireBeacon
    );

    return () => {
      window.removeEventListener(
        "beforeunload",
        fireBeacon
      );

      window.removeEventListener(
        "pagehide",
        fireBeacon
      );
    };
  }, [
    assessmentId,
    studentId,
    data,
    school_id,
    client_id,
  ]);

  /* ===== Timer tick ===== */
  useEffect(() => {
    if (!deadlineMs) return undefined;

    const tick = () => {
      const remaining = Math.max(
        0,
        Math.floor((deadlineMs - Date.now()) / 1000),
      );
      setSecondsLeft(remaining);
      return remaining;
    };

    if (tick() === 0) return undefined;
    const id = setInterval(() => {
      if (tick() === 0) clearInterval(id);
    }, 1000);

    return () => clearInterval(id);
  }, [deadlineMs]);

  const timerExpired = secondsLeft === 0;
  const isLocked = timerExpired || submitting;

  /* ===== Auto-submit on timer expiry ===== */
  useEffect(() => {
    if (
      timerExpired &&
      !autoSubmitFiredRef.current &&
      data?.questions?.length
    ) {
      autoSubmitFiredRef.current = true;

      const draft = readDraft(assessmentId, studentId);
      const latestAnswers = draft?.answers || answers;

      submitAssessment({ auto: true, answersOverride: latestAnswers });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerExpired, data]);

  /* ===== Success countdown ===== */
  useEffect(() => {
    if (submittedSuccess.open) {
      setSuccessCountdown(SUCCESS_REDIRECT_SECONDS);
      successCountdownRef.current = setInterval(() => {
        setSuccessCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(successCountdownRef.current);
            navigateToResult();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(successCountdownRef.current);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedSuccess.open]);

  const navigateToResult = useCallback(() => {
    if (successCountdownRef.current) {
      clearInterval(successCountdownRef.current);
    }
    const { redirectPath, redirectState } = submittedSuccess;
    if (redirectPath) {
      navigate(
        redirectPath,
        redirectState ? { state: redirectState } : undefined,
      );
    }
  }, [submittedSuccess, navigate]);

  /* ===== Handlers ===== */
  const handleSelectOption = (questionId, letter) => {
    if (isLocked) return;
    setAnswers((prev) => ({ ...prev, [questionId]: letter }));
  };

  const handleCancel = () => {
    if (isLocked) return;
    const hasAnyAnswers = Object.values(answers).some((v) => v);
    if (hasAnyAnswers) {
      setShowCancelModal(true);
      return;
    }
    navigate(-1);
  };

  const confirmCancel = () => {
    setShowCancelModal(false);
    clearDraftFor(assessmentId, studentId);
    navigate(-1);
  };

  const handleDismissCancelModal = () => setShowCancelModal(false);

  const handleSubmit = () => {
    if (isLocked) return;
    if (!data?.questions?.length) return;
    setShowConfirmModal(true);
  };

  const handleCancelConfirm = () => setShowConfirmModal(false);

  const confirmSubmit = () => {
    setShowConfirmModal(false);
    submitAssessment({ auto: false });
  };

  /* Shared submit path. Always sends every question, null for unanswered. */
  const submitAssessment = useCallback(
    async ({ auto, answersOverride }) => {
      if (submitting) return;
      setSubmitting(true);
      setError(null);

      const answersToSend = answersOverride || answers;

      try {
        const submissionAnswers = buildAnswersArray(answersToSend);

        const payload = {
          school_id: data?.assessment?.school_id || school_id,
          student_id: studentId,
          client_id: data?.assessment?.client_id || client_id,
          assessment_id: assessmentId,
          answers: submissionAnswers,
          submission_source: auto ? "auto" : "manual",
          auto_submitted: !!auto,
        };

        const response = await AxiosInstance.post(
          `/book/student/submitAssessment`,
          payload,
        );

        const ok =
          response?.data?.status === true || response?.data?.success === true;

        if (ok) {
          submitFiredRef.current = true;

          const resultData = response?.data?.data || {};
          const resultAssessmentId = resultData.assessment_id || assessmentId;

          const redirectPath = isRemedial
            ? "/user/viewStudentRemedialResult"
            : "/user/viewStudentAssessmentResult";

          clearDraftFor(assessmentId, studentId);

          setSubmittedSuccess({
            open: true,
            message:
              response?.data?.message ||
              translations?.labels?.assessmentSubmittedTitle ||
              "Your assessment has been submitted successfully.",
            redirectPath,
            redirectState: {
              assessment_id: resultAssessmentId,
              student_id: studentId,
              type: isRemedial ? "remedial" : "assessment",
            },
          });
        } else {
          setErrorModal({
            open: true,
            message:
              response?.data?.message || "Failed to submit assessment",
          });
        }
      } catch (err) {
        console.error("Error submitting assessment:", err);
        setErrorModal({
          open: true,
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to submit assessment",
        });
      } finally {
        setSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data,
      answers,
      assessmentId,
      studentId,
      school_id,
      client_id,
      isRemedial,
      submitting,
    ],
  );

  const getOptionText = (question, letter) => {
    if (!letter) return "";
    const key = `option_${letter.toLowerCase()}`;
    return question[key];
  };

  /* ===== Derived ===== */
  const answeredCount = useMemo(
    () => (data?.questions || []).filter((q) => answers[q.id]).length || 0,
    [data, answers],
  );
  const totalQuestions = data?.questions?.length || 0;

  const formatMmSs = (totalSec) => {
    if (totalSec == null) return null;
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return {
      m: String(m).padStart(2, "0"),
      s: String(s).padStart(2, "0"),
    };
  };
  const timeParts = formatMmSs(secondsLeft);

  const timerWarning =
    secondsLeft != null && secondsLeft <= 60 && secondsLeft > 0;
  const timerEnded = secondsLeft === 0;

  /* ===== Render guards ===== */
  if (loading) {
    return (
      <div className={styles.loaderWrapper}>
        <div className={styles.spinner}></div>
        <p>{translations?.labels?.loading || "Loading assessment..."}</p>
      </div>
    );
  }

  if (submittedSuccess.open) {
    return (
      <div className={styles.successWrapper}>
        <h1 className={styles.successTitle}>
          {submittedSuccess.message ||
            translations?.labels?.assessmentSubmittedTitle ||
            "Your assessment has been submitted successfully."}
        </h1>

        <p className={styles.successSubtitle}>
          {translations?.labels?.autoRedirectBefore ||
            "You will be automatically redirected to the Result page in "}
          <strong>{successCountdown}</strong>
          {translations?.labels?.autoRedirectMiddle || " seconds, or click "}
          <button
            type="button"
            onClick={navigateToResult}
            className={styles.successResultLink}
          >
            {translations?.labels?.viewResult || "View Result"}
          </button>
          {translations?.labels?.autoRedirectAfter || " to continue now."}
        </p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className={styles.errorWrapper}>
        <p>⚠ {error}</p>
        <button onClick={() => navigate(-1)} className={styles.cancelButton}>
          {translations?.labels?.back || "Back"}
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { assessment, questions } = data;

  const confirmHeading = (
    translations?.labels?.answeredOutOfTotal ||
    "You have answered {answered} out of {total} questions"
  )
    .replace("{answered}", String(answeredCount))
    .replace("{total}", String(totalQuestions));

  const confirmBody =
    translations?.labels?.submitConfirmMessage ||
    "Please review your answers carefully before submitting. Once submitted, you will not be able to make any further changes.";

  return (
    <div className={styles.container}>
      {/* Cancel modal */}
      {showCancelModal && (
        <div
          className={styles.modalOverlay}
          onClick={handleDismissCancelModal}
          role="presentation"
        >
          <div
            className={styles.modalCard}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-title"
          >
            <div className={styles.modalIcon}>
              <span>!</span>
            </div>
            <h2 id="cancel-title" className={styles.modalTitle}>
              {translations?.labels?.areYouSure || "Are you sure?"}
            </h2>
            <p className={styles.modalMessage}>
              {translations?.labels?.cancelConfirmMessage ||
                "Are you sure you want to cancel? Your answers will not be saved and the assessment will need to be restarted."}
            </p>
            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={confirmCancel}
                className={styles.modalConfirmButton}
              >
                {translations?.labels?.yesLeave || "Yes, Leave"}
              </button>
              <button
                type="button"
                onClick={handleDismissCancelModal}
                className={styles.modalCancelButton}
              >
                {translations?.labels?.noStay || "No, Stay"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error popup */}
      <PopupModal
        open={errorModal.open}
        title={translations?.labels?.modalFailedTitle || "Failed"}
        message={errorModal.message}
        okText={translations?.labels?.ok || "OK"}
        onOk={() => setErrorModal({ open: false, message: "" })}
        onClose={() => setErrorModal({ open: false, message: "" })}
      />

      {/* Topic info + timer pill */}
      <div className={styles.topicInfo}>
        <div className={styles.topicLeft}>
          <p>
            <strong>{translations?.labels?.topic || "Topic"} :</strong>{" "}
            {assessment?.topic || "-"}
          </p>
          <p>
            <strong>{translations?.labels?.subTopic || "Sub Topic"} :</strong>{" "}
            {assessment?.sub_topic || "-"}
          </p>
        </div>

        <div className={styles.topicRight}>
          <p>
            <strong>{translations?.labels?.subject || "Subject"} :</strong>{" "}
            {assessment?.subject || "-"}
          </p>
          <p>
            <strong>{translations?.labels?.class || "Class"} :</strong>{" "}
            {assessment?.class_name || "-"}
            {assessment?.section_id ? ` - ${assessment.section_id}` : ""}
          </p>

          {timeParts && (
            <div
              className={`${styles.timerPill} ${timerEnded ? styles.timerPillEnded : ""
                } ${timerWarning ? styles.timerPillWarning : ""}`}
              role="timer"
              aria-live="polite"
              aria-atomic="true"
            >
              <FaRegClock className={styles.timerIcon} />
              <span className={styles.timerLabel}>
                {translations?.labels?.timeLeftToComplete ||
                  "Time left to complete:"}
              </span>
              <span className={styles.timerValue}>
                {timeParts.m} {translations?.labels?.minShort || "min"} :{" "}
                {timeParts.s} {translations?.labels?.secShort || "sec"}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Questions list */}
      <div
        className={`${styles.questionsList} ${isLocked ? styles.questionsListLocked : ""
          }`}
      >
        {questions?.map((question, index) => {
          const selected = answers[question.id];

          return (
            <div key={question.id} className={styles.questionCard}>
              <p className={styles.questionText}>
                {index + 1}. {question.assessment_question}
              </p>

              <div className={styles.optionsGrid}>
                {OPTION_LETTERS.map((letter) => {
                  const optionText = getOptionText(question, letter);
                  if (!optionText) return null;

                  const isSelected = selected === letter;

                  return (
                    <label
                      key={letter}
                      className={`${styles.option} ${isSelected ? styles.optionSelected : ""
                        } ${isLocked ? styles.optionDisabled : ""}`}
                    >
                      <input
                        type="radio"
                        name={`question-${question.id}`}
                        value={letter}
                        checked={isSelected}
                        onChange={() => handleSelectOption(question.id, letter)}
                        disabled={isLocked}
                        className={styles.radioInput}
                      />
                      <span
                        className={`${styles.radio} ${isSelected ? styles.radioSelected : ""
                          }`}
                      ></span>
                      <span className={styles.optionLabel}>
                        {letter}) {optionText}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <button
          type="button"
          onClick={handleCancel}
          className={styles.cancelButton}
          disabled={isLocked}
        >
          {translations?.labels?.cancel || "Cancel"}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className={styles.submitButton}
          disabled={isLocked}
        >
          {submitting
            ? translations?.labels?.submitting || "Submitting..."
            : translations?.labels?.submit || "Submit"}
        </button>
      </div>

      {/* Confirmation modal */}
      {showConfirmModal && (
        <div
          className={styles.modalOverlay}
          onClick={handleCancelConfirm}
          role="presentation"
        >
          <div
            className={styles.modalCard}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
          >
            <div className={styles.modalIcon}>
              <span>!</span>
            </div>
            <h2 id="confirm-title" className={styles.modalTitle}>
              {confirmHeading}
            </h2>
            <p className={styles.modalMessage}>{confirmBody}</p>
            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={handleCancelConfirm}
                className={styles.modalCancelButton}
                disabled={submitting}
              >
                {translations?.labels?.cancel || "Cancel"}
              </button>
              <button
                type="button"
                onClick={confirmSubmit}
                className={styles.modalConfirmButton}
                disabled={submitting}
              >
                {translations?.labels?.confirm || "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewAssessmentTest;